import {useMemo} from 'react';
import {SubsidyDetails, SubsidyType} from 'src/types/SubsidyDetails';
import {useAppSelector} from 'src/redux/hooks';
import {BalanceTypes} from 'src/types/serverTypes/Account';
import AccountStore from 'src/stores/AccountStore';
import {parseDayTimeRestrictions} from 'src/utils/subsidyTimeValidation';
import {formatSubsidyDate} from 'src/utils/subsidyCardUtils';
import Localized from 'src/constants/AppStrings';
import moment from 'moment';

const formatTimeToAMPM = (time24: string): string => {
  try {
    const timeMoment = moment(time24, 'HH:mm');
    if (!timeMoment.isValid()) {
      return time24;
    }
    return timeMoment.format('h:mma');
  } catch (error) {
    return time24;
  }
};

/**
 * Formats recurrence from API format (DAILY, WEEKLY, MONTHLY) to display format
 */
const formatRecurrence = (recurrence: string): string => {
  const upperRecurrence = recurrence.toUpperCase();
  switch (upperRecurrence) {
    case 'DAILY':
      return Localized.Labels.daily;
    case 'WEEKLY':
      return Localized.Labels.weekly;
    case 'MONTHLY':
      return Localized.Labels.monthly;
    default:
      return recurrence;
  }
};

export const useSubsidyDetails = (): {
  subsidyDetails: SubsidyDetails | null;
  isLoading: boolean;
} => {
  const displayBalances = useAppSelector(
    (state) => state.account.account.displayBalances,
  );
  const account = useAppSelector((state) => state.account.account);

  const subsidyDetails = useMemo(() => {
    const subsidyBalance = displayBalances.find(
      (balance) =>
        balance.type === BalanceTypes.RollOver ||
        balance.type === BalanceTypes.TopOff,
    );

    if (!subsidyBalance) {
      return null;
    }

    const pantryGroup = account.pantryGroup;

    const validDaysTimes: string[] = [];
    if (pantryGroup?.dayTimeRestrictions) {
      const restrictions = parseDayTimeRestrictions(
        pantryGroup.dayTimeRestrictions,
      );
      restrictions.forEach((restriction) => {
        if (restriction.timeRanges.length > 0) {
          restriction.timeRanges.forEach((range) => {
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const dayName = dayNames[restriction.dayOfWeek];
            const startTime = formatTimeToAMPM(range.start);
            const endTime = formatTimeToAMPM(range.end);
            validDaysTimes.push(`${dayName}: ${startTime}-${endTime}`);
          });
        }
      });
    }

    const subsidyDetails: SubsidyDetails = {
      balance: subsidyBalance.balance,
      currency: subsidyBalance.currency || AccountStore.getCurrency(),
      holderName:
        `${account.firstName || ''} ${account.lastName || ''}`.trim() || 'User',
      endDate: pantryGroup?.endDate
        ? formatSubsidyDate(pantryGroup.endDate)
        : undefined,
      groupName: pantryGroup?.name || 'Subsidy Group',
      validDaysTimes: validDaysTimes.length > 0 ? validDaysTimes : undefined,
      recurrence: pantryGroup?.recurrence
        ? formatRecurrence(pantryGroup.recurrence)
        : undefined,
      subsidyType:
        subsidyBalance.type === BalanceTypes.RollOver
          ? SubsidyType.Rollover
          : SubsidyType.TopOff,
    };

    return subsidyDetails;
  }, [displayBalances, account]);

  return {
    subsidyDetails,
    isLoading: false,
  };
};
